import React, { useState } from 'react';
import { useUIKit } from '@tencentcloud/uikit-base-component-react';
import BuildVersionBadge from '../../components/BuildVersionBadge';
import './index.scss';

// 后端 API 地址
const API_BASE_URL = 'https://chat-backend-worker.pafonsecnhtt80.workers.dev/api';
// 固定腾讯云的 SDKAppID
const DEFAULT_APP_ID = '20032118';

interface AuthProps {
  onSuccess: (data: {
    user_id: string;
    userSig: string;
    app_id: number;
    nickname: string;
  }) => void;
}

export default function AuthPage({ onSuccess }: AuthProps) {
  const { t, language, setLanguage } = useUIKit();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 表单状态
  const [appId, setAppId] = useState(DEFAULT_APP_ID);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const endpoint = isLogin ? '/login' : '/register';
    const payload = isLogin
      ? { app_id: appId, username, password }
      : { app_id: appId, username, password, nickname: nickname || username };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': language // 传入当前的语言状态给后端
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.message || data.error || 'Authentication failed');
      }

      // IM userSig is signed with Worker env TENCENT_CHAT_SDK_APP_ID — must match SDKAppID in UIKit.
      const sdkAppId = Number(data.data.sdk_app_id);
      if (!Number.isFinite(sdkAppId) || sdkAppId <= 0) {
        throw new Error('Missing or invalid sdk_app_id from server; update chat-cfworker auth response.');
      }

      if (!data?.data?.user_id || !data?.data?.userSig) {
        throw new Error('Invalid login payload from server: missing user_id or userSig.');
      }

      console.log('=== DEBUG USERSIG ===');
      console.log('User ID:', data.data.user_id);
      console.log('SDK App ID:', sdkAppId);
      console.log('UserSig:', data.data.userSig);
      console.log('=====================');

      onSuccess({
        user_id: data.data.user_id,
        userSig: data.data.userSig,
        app_id: sdkAppId,
        nickname: data.data.nickname || username,
      });

    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <header className="chat-header">
        <div>Tencent Cloud IM</div>
        <select className="chat-header__lang-select"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          <option value="zh-CN">简体中文</option>
          <option value="zh-TW">繁體中文</option>
          <option value="en-US">English</option>
          <option value="ja-JP">日本語</option>
          <option value="ko-KR">한국어</option>
        </select>
      </header>

      <div className="auth-container">
        <div className="auth-box">
          <h2>{isLogin ? t('Login_Title') : t('Register_Title')}</h2>
          
          {error && <div className="error-message">{error}</div>}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="appId">{t('AppID_Label')}</label>
              <input
                id="appId"
                type="text"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                placeholder={t('AppID_Placeholder') as string}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="username">{t('Username_Label')}</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('Username_Placeholder') as string}
                required
              />
            </div>

            {!isLogin && (
              <div className="form-group">
                <label htmlFor="nickname">{t('Nickname_Label')}</label>
                <input
                  id="nickname"
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder={t('Nickname_Placeholder') as string}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password">{t('Password_Label')}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('Password_Placeholder') as string}
                required
              />
            </div>

            <button 
              type="submit" 
              className="auth-button"
              disabled={loading || !username || !password || !appId}
            >
              {loading ? t('Processing') : (isLogin ? t('Login_Btn') : t('Register_Btn'))}
            </button>
          </form>

          <div className="auth-switch">
            {isLogin ? t('No_Account') : t('Has_Account')}
            <button 
              type="button" 
              onClick={() => {
                setIsLogin(!isLogin);
                setError(null);
              }}
            >
              {isLogin ? t('Switch_Register') : t('Switch_Login')}
            </button>
          </div>
        </div>
        <BuildVersionBadge variant="footer" />
      </div>
    </div>
  );
}
